(() => {
  'use strict';

  class ProductPage extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-product-json]');
      if (!data) return;

      try {
        this.product = JSON.parse(data.textContent);
      } catch (error) {
        console.error('Unable to read product data.', error);
        return;
      }

      this.classList.add('product-js');

      this.currency = this.dataset.currency || 'USD';
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.labels = {
        add: this.dataset.labelAdd || 'Add to cart',
        soldOut: this.dataset.labelSoldOut || 'Sold out',
        unavailable: this.dataset.labelUnavailable || 'Unavailable',
        subscribe: this.dataset.labelSubscribe || 'Subscribe & Save',
        oneTime: this.dataset.labelOneTime || 'One-time purchase',
        from: this.dataset.labelFrom || 'From',
        subscribeFrom: this.dataset.labelSubscribeFrom || 'Subscribe from',
        save: this.dataset.labelSave || 'save',
        planUnavailable: this.dataset.labelPlanUnavailable || 'Not available for this option',
        perDelivery: this.dataset.labelPerDelivery || '[price] per delivery',
      };
      this.form = this.querySelector('.pdp-form');
      this.variantInput = this.querySelector('[data-variant-id]');
      this.variantFallback = this.querySelector('[data-variant-fallback]');
      this.optionSelects = [...this.querySelectorAll('[data-option-position]')];
      this.purchaseInputs = [...this.querySelectorAll('input[name^="purchase_type_"]')];
      this.planSelect = this.querySelector('[data-selling-plan]');
      this.planChoices = [...this.querySelectorAll('[data-plan-choice]')];
      this.planChoicesContainer = this.querySelector('[data-plan-choices]');
      this.planSelectFallback = this.querySelector('[data-plan-select-fallback]');
      this.quantityInput = this.querySelector('input[name="quantity"]');

      if (this.variantInput) this.variantInput.name = 'id';
      if (this.variantFallback) this.variantFallback.disabled = true;
      if (this.planChoices.length && this.planSelect) {
        this.planSelect.removeAttribute('name');
        this.planChoicesContainer.hidden = false;
        this.planSelectFallback.hidden = true;
      }

      const requestedPlan = new URL(window.location.href).searchParams.get('selling_plan');
      if (requestedPlan && this.planSelect?.querySelector(`option[value="${CSS.escape(requestedPlan)}"]`)) {
        this.planSelect.value = requestedPlan;
        const requestedChoice = this.planChoices.find((choice) => choice.value === requestedPlan);
        if (requestedChoice) requestedChoice.checked = true;
        const subscriptionInput = this.purchaseInputs.find((input) => input.value === 'subscription');
        if (subscriptionInput) subscriptionInput.checked = true;
      }

      this.bindGallery();
      this.bindOptions();
      this.bindPurchaseType();
      this.bindQuantity();
      this.bindInformationTabs();
      this.updateVariant(false);
    }

    money(cents) {
      const match = this.moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!match) return (Number(cents || 0) / 100).toFixed(2);

      const withDelimiters = (value, precision = 2, thousands = ',', decimal = '.') => {
        const parts = (Number(value || 0) / 100).toFixed(precision).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.join(decimal);
      };
      const values = {
        amount: withDelimiters(cents),
        amount_no_decimals: withDelimiters(cents, 0),
        amount_with_comma_separator: withDelimiters(cents, 2, '.', ','),
        amount_no_decimals_with_comma_separator: withDelimiters(cents, 0, '.', ','),
        amount_with_apostrophe_separator: withDelimiters(cents, 2, "'", '.'),
      };
      return this.moneyFormat.replace(match[0], values[match[1]] ?? values.amount);
    }

    get selectedVariant() {
      if (!this.product?.variants?.length) return null;
      if (!this.optionSelects.length) {
        const selectedId = Number(this.variantInput?.value);
        return this.product.variants.find((variant) => variant.id === selectedId) || this.product.variants[0];
      }

      const options = this.optionSelects.map((select) => select.value);
      return this.product.variants.find((variant) => variant.options.every((option, index) => option === options[index])) || null;
    }

    get isSubscription() {
      return this.purchaseInputs.find((input) => input.checked)?.value === 'subscription';
    }

    get allocation() {
      const variant = this.selectedVariant;
      if (!variant?.selling_plan_allocations?.length || !this.planSelect) return null;
      const planId = Number(this.planSelect.value);
      return variant.selling_plan_allocations.find((item) => item.selling_plan_id === planId) || variant.selling_plan_allocations[0];
    }

    bindGallery() {
      const mediaItems = [...this.querySelectorAll('[data-media-slide]')];
      if (!mediaItems.length) return;
      mediaItems.forEach((item) => {
        const frame = item.querySelector('iframe');
        if (frame) frame.dataset.mediaSrc = frame.src;
      });
      this.querySelectorAll('[data-image]').forEach((button) => {
        button.addEventListener('click', () => {
          mediaItems.forEach((item) => {
            const active = item.dataset.mediaSlide === button.dataset.mediaId;
            item.hidden = !active;
            if (!active) item.querySelector('video')?.pause();
            const frame = item.querySelector('iframe');
            if (frame && !active && frame.src !== 'about:blank') frame.src = 'about:blank';
            if (frame && active && frame.src === 'about:blank' && frame.dataset.mediaSrc) frame.src = frame.dataset.mediaSrc;
          });

          this.querySelectorAll('[data-image]').forEach((item) => {
            const active = item === button;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-pressed', String(active));
          });
        });
      });
    }

    bindOptions() {
      this.optionSelects.forEach((select) => select.addEventListener('change', () => this.updateVariant(true)));
      this.planSelect?.addEventListener('change', () => this.renderPrices());
      this.planChoices.forEach((choice) => choice.addEventListener('change', () => {
        if (!choice.checked || !this.planSelect) return;
        this.planSelect.value = choice.value;
        const subscriptionInput = this.purchaseInputs.find((input) => input.value === 'subscription');
        if (subscriptionInput) subscriptionInput.checked = true;
        this.syncPurchaseType();
        this.renderPrices();
      }));
    }

    bindPurchaseType() {
      this.purchaseInputs.forEach((input) => input.addEventListener('change', () => {
        this.syncPurchaseType();
        this.renderPrices();
      }));
    }

    bindQuantity() {
      this.querySelectorAll('[data-quantity]').forEach((button) => {
        button.addEventListener('click', () => {
          if (!this.quantityInput) return;
          const step = Number(this.quantityInput.step) || 1;
          const minimum = Number(this.quantityInput.min) || 1;
          const maximum = this.quantityInput.max ? Number(this.quantityInput.max) : Infinity;
          const current = Number(this.quantityInput.value) || minimum;
          this.quantityInput.value = button.dataset.quantity === 'up' ? Math.min(maximum, current + step) : Math.max(minimum, current - step);
        });
      });
    }

    bindInformationTabs() {
      this.querySelectorAll('[data-product-tabs]').forEach((tabset) => {
        const tabs = [...tabset.querySelectorAll('[role="tab"]')];
        const panels = tabs.map((tab) => this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`));
        if (!tabs.length || panels.some((panel) => !panel)) return;

        const activate = (tab, moveFocus = false) => {
          tabs.forEach((candidate, index) => {
            const selected = candidate === tab;
            candidate.setAttribute('aria-selected', String(selected));
            candidate.tabIndex = selected ? 0 : -1;
            panels[index].hidden = !selected;
          });

          if (moveFocus) tab.focus();

          const rail = tab.closest('[role="tablist"]');
          if (rail && rail.scrollWidth > rail.clientWidth) {
            const left = tab.offsetLeft - ((rail.clientWidth - tab.offsetWidth) / 2);
            rail.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
          }
        };

        tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => activate(tab));
          tab.addEventListener('keydown', (event) => {
            let nextIndex;
            if (['ArrowRight', 'ArrowDown'].includes(event.key)) nextIndex = (index + 1) % tabs.length;
            if (['ArrowLeft', 'ArrowUp'].includes(event.key)) nextIndex = (index - 1 + tabs.length) % tabs.length;
            if (event.key === 'Home') nextIndex = 0;
            if (event.key === 'End') nextIndex = tabs.length - 1;
            if (nextIndex === undefined) return;
            event.preventDefault();
            activate(tabs[nextIndex], true);
          });
        });
      });
    }

    updateVariant(updateUrl = true) {
      const variant = this.selectedVariant;
      if (!variant) {
        this.setUnavailable();
        return;
      }

      if (this.variantInput) {
        this.variantInput.value = variant.id;
        this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (this.quantityInput && variant.quantity_rule) {
        const minimum = Number(variant.quantity_rule.min) || 1;
        const increment = Number(variant.quantity_rule.increment) || 1;
        const maximum = Number(variant.quantity_rule.max) || Infinity;
        this.quantityInput.min = String(minimum);
        this.quantityInput.step = String(increment);
        if (Number.isFinite(maximum)) this.quantityInput.max = String(maximum);
        else this.quantityInput.removeAttribute('max');

        const current = Number(this.quantityInput.value) || minimum;
        const normalized = minimum + (Math.ceil(Math.max(0, current - minimum) / increment) * increment);
        this.quantityInput.value = String(Math.min(maximum, Math.max(minimum, normalized)));
      }
      if (updateUrl && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      this.updateSellingPlans(variant);
      this.updateFeaturedMedia(variant);
      this.updatePaymentTerms(variant);
      this.renderPrices();

      this.dispatchEvent(new CustomEvent('variant:change', {
        bubbles: true,
        detail: { variant },
      }));

      const button = this.querySelector('[data-add-to-cart]');
      const label = this.querySelector('[data-add-label]');
      if (button) button.disabled = !variant.available;
      if (label) label.textContent = variant.available ? this.labels.add : this.labels.soldOut;
    }

    updateSellingPlans(variant) {
      if (!this.planSelect) return;
      const allocations = variant.selling_plan_allocations || [];
      const planIds = new Set(allocations.map((item) => item.selling_plan_id));

      [...this.planSelect.options].forEach((option) => {
        if (!option.value) {
          option.disabled = true;
          return;
        }
        const allocation = allocations.find((item) => item.selling_plan_id === Number(option.value));
        option.disabled = !allocation;
        option.textContent = allocation ? `${option.dataset.baseLabel} — ${this.money(allocation.price)}` : option.dataset.baseLabel;
      });

      if (!planIds.has(Number(this.planSelect.value))) {
        const firstEnabled = [...this.planSelect.options].find((option) => !option.disabled);
        this.planSelect.value = firstEnabled?.value || '';
      }

      this.planChoices.forEach((choice) => {
        const allocation = allocations.find((item) => item.selling_plan_id === Number(choice.value));
        choice.dataset.planAvailable = String(Boolean(allocation));
        const price = choice.closest('.pdp-plan-choice')?.querySelector('[data-plan-choice-price]');
        if (price) price.textContent = allocation ? this.labels.perDelivery.replace('[price]', this.money(allocation.price)) : this.labels.planUnavailable;
      });

      let selectedChoice = this.planChoices.find((choice) => choice.value === this.planSelect.value && choice.dataset.planAvailable === 'true');
      if (!selectedChoice) selectedChoice = this.planChoices.find((choice) => choice.dataset.planAvailable === 'true');
      this.planChoices.forEach((choice) => { choice.checked = choice === selectedChoice; });
      if (selectedChoice) this.planSelect.value = selectedChoice.value;

      const hasPlans = allocations.length > 0;
      const oneTimeInput = this.purchaseInputs.find((input) => input.value === 'one-time');
      const subscriptionInput = this.purchaseInputs.find((input) => input.value === 'subscription');
      if (oneTimeInput) oneTimeInput.disabled = Boolean(variant.requires_selling_plan);
      if (subscriptionInput) subscriptionInput.disabled = !hasPlans;
      if (variant.requires_selling_plan && hasPlans && subscriptionInput) {
        subscriptionInput.checked = true;
      } else if (!hasPlans && this.isSubscription && oneTimeInput) {
        oneTimeInput.checked = true;
      }
      this.syncPurchaseType();
    }

    syncPurchaseType() {
      const active = this.isSubscription && Boolean(this.allocation);
      const planRow = this.querySelector('[data-plan-row]');
      if (planRow) planRow.hidden = !active;
      if (this.planSelect) this.planSelect.disabled = !active;
      this.planChoices.forEach((choice) => {
        choice.disabled = !active || choice.dataset.planAvailable !== 'true';
      });

      const reassurance = this.querySelector('.pdp-reassurance');
      const consent = this.querySelector('.pdp-subscription-consent');
      const terms = this.querySelector('.pdp-payment-terms');
      if (reassurance) reassurance.hidden = !active;
      if (consent) consent.hidden = !active;
      if (terms) terms.hidden = !active;
    }

    renderPrices() {
      const variant = this.selectedVariant;
      if (!variant) return;
      const allocation = this.allocation;
      const activePrice = this.isSubscription && allocation ? allocation.price : variant.price;
      const saving = allocation && variant.price ? Math.round(((variant.price - allocation.price) / variant.price) * 100) : 0;

      this.setText('[data-product-price]', this.money(variant.price));
      this.setText('[data-one-time-price]', this.money(variant.price));
      this.setText('[data-button-price]', this.money(activePrice));
      this.setText('[data-purchase-label]', this.isSubscription && allocation ? this.labels.subscribe : this.labels.oneTime);
      this.setText('[data-subscription-price]', allocation ? `${this.labels.from} ${this.money(allocation.price)}` : this.labels.planUnavailable);

      const compare = this.querySelector('[data-compare-price]');
      if (compare) {
        compare.hidden = !(variant.compare_at_price > variant.price);
        compare.textContent = variant.compare_at_price ? this.money(variant.compare_at_price) : '';
      }

      const summary = this.querySelector('[data-subscription-summary]');
      if (summary) {
        summary.hidden = !allocation;
        summary.textContent = allocation ? `${this.labels.subscribeFrom} ${this.money(allocation.price)}${saving ? ` (${this.labels.save} ${saving}%)` : ''}` : '';
      }

      const badge = this.querySelector('[data-discount-badge]');
      if (badge) {
        badge.hidden = saving === 0;
        badge.textContent = `${saving}%`;
      }
    }

    updateFeaturedMedia(variant) {
      if (!variant.featured_media?.id) return;
      const thumbnail = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
      thumbnail?.click();
    }

    updatePaymentTerms(variant) {
      const paymentTerms = this.querySelector('shopify-payment-terms');
      if (paymentTerms) paymentTerms.setAttribute('variant-id', String(variant.id));
    }

    setUnavailable() {
      const button = this.querySelector('[data-add-to-cart]');
      if (button) button.disabled = true;
      this.setText('[data-add-label]', this.labels.unavailable);
      this.setText('[data-button-price]', '');
    }

    setText(selector, value) {
      const element = this.querySelector(selector);
      if (element) element.textContent = value;
    }
  }

  if (!customElements.get('product-page')) customElements.define('product-page', ProductPage);
})();
